import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { FileSignature, Calendar, DollarSign, ChevronRight, Loader2, Building2, Search } from 'lucide-react';
import { agreementsAPI } from '../services/api';
import { useCompanyStore } from '../context/companyStore';
import CompanyFilter from '../components/common/CompanyFilter';
import { useDisplay } from '../hooks/useDisplay';

export default function AgreementsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const lastRefreshTimestamp = useRef(null);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const d = useDisplay();

  // Multi-company support
  const { selectedCompanyId, hasMultipleCompanies } = useCompanyStore();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['agreements', selectedCompanyId],
    queryFn: () => agreementsAPI.list({ companyId: selectedCompanyId }),
  });

  // Sync search to URL so it survives back-navigation from the detail page.
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    setSearchParams(params, { replace: true });
  }, [search, setSearchParams]);

  // Auto-refresh when returning from detail page
  useEffect(() => {
    const refreshTimestamp = location.state?.refresh;
    if (refreshTimestamp && refreshTimestamp !== lastRefreshTimestamp.current) {
      lastRefreshTimestamp.current = refreshTimestamp;
      setSearch(searchParams.get('search') || '');
      refetch();
    }
  }, [location.state?.refresh, refetch, searchParams]);

  const allAgreements = data?.data?.agreements || [];

  // Filter by search
  const agreements = allAgreements.filter(agreement => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return agreement.name?.toLowerCase().includes(searchLower) ||
           agreement.company?.name?.toLowerCase().includes(searchLower) ||
           agreement.type?.name?.toLowerCase().includes(searchLower);
  });

  return (
    <div className="animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Service Agreements</h1>
          <p className="text-neutral-600 dark:text-neutral-400">View your active service agreements</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
          <input
            type="text"
            placeholder="Search agreements..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
        <CompanyFilter />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {isLoading ? (
          <div className="col-span-2 p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
            <p className="text-neutral-500 dark:text-neutral-400">Loading agreements...</p>
          </div>
        ) : agreements.length === 0 ? (
          <div className="col-span-2 card p-8 text-center text-neutral-500 dark:text-neutral-400">
            <FileSignature className="w-12 h-12 mx-auto mb-3 text-neutral-300 dark:text-neutral-600" />
            <p>No agreements found</p>
          </div>
        ) : (
          agreements.map((agreement) => (
            <div
              key={agreement.id}
              onClick={() => {
                sessionStorage.setItem('agreementsReturnUrl', window.location.pathname + window.location.search);
                navigate(`/agreements/${agreement.id}`);
              }}
              className="card p-5 cursor-pointer hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-neutral-900 dark:text-white truncate">{agreement.name}</h3>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">#{agreement.id}</p>
                </div>
                <div className="flex items-center gap-2 ml-2">
                  <span className={`badge ${agreement.cancelledFlag ? 'badge-danger' : 'badge-success'}`}>
                    {agreement.cancelledFlag ? 'Cancelled' : 'Active'}
                  </span>
                  <ChevronRight className="w-5 h-5 text-neutral-400" />
                </div>
              </div>
              <div className="space-y-2 text-sm">
                {d.visible('agreementsList.row.startDate') && (
                <div className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400">
                  <Calendar className="w-4 h-4" />
                  <span>{d.label('agreementsList.row.startDate')}: {agreement.startDate ? new Date(agreement.startDate).toLocaleDateString() : 'N/A'}</span>
                </div>
                )}
                {d.visible('agreementsList.row.type') && (
                <div className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400">
                  <DollarSign className="w-4 h-4" />
                  <span>{d.label('agreementsList.row.type')}: {agreement.type?.name || 'N/A'}</span>
                </div>
                )}
                {hasMultipleCompanies && agreement.company?.name && (
                  <div className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400">
                    <Building2 className="w-4 h-4" />
                    <span>{agreement.company.name}</span>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
